// Presentation layout for the world maps: where each agent's house sits,
// the courier routes between them, and drawn bridge overlays.
// Keys are agent ids (slugified names — see slugify in worlds.js).
// Everything here is % of the world image (1448x1086, 4:3).
// Swap artwork → retune here; components never hardcode positions.

// ---- house positions ----
// manager: true renders the plaza standard (star flag) instead of an invisible house hotspot.
export const AGENT_LAYOUT = {
  leads: {
    "prospect-miner": { x: 17.8, y: 27.5 },
    "social-listener": { x: 28.3, y: 27 },
    "outbound-sequencer": { x: 64.8, y: 24.5 },
    "cold-caller": { x: 73.6, y: 24.5 },
    "social-prospector": { x: 82.5, y: 24.5 },
    "referral-agent": { x: 69.3, y: 37.4 },
    "reactivation-agent": { x: 78.7, y: 37.4 },
    "newsletter-agent": { x: 16.2, y: 65 },
    "content-repurposer": { x: 26.2, y: 64.5 },
    "engagement-agent": { x: 15.6, y: 78.7 },
    "campaign-agent": { x: 26.2, y: 78.7 },
    "speed-to-lead": { x: 62.2, y: 67.5 },
    "qualifier": { x: 71.5, y: 67.5 },
    "booking-agent": { x: 80.4, y: 67.5 },
    "call-intelligence": { x: 57.2, y: 81 },
    "analysis-builder": { x: 65.8, y: 81 },
    "program-builder": { x: 74.3, y: 81 },
    "stage-nurture": { x: 82.8, y: 81 },
    "lead-manager": { x: 49.5, y: 50, manager: true },
  },
  clients: {
    "onboarding-agent": { x: 17.8, y: 21.5 },
    "intake-agent": { x: 28.8, y: 21 },
    "systems-setup-agent": { x: 39.7, y: 21 },
    "assessment-and-goals": { x: 70.9, y: 21.5 },
    "roadmap-and-priorities": { x: 82.8, y: 21.5 },
    "sales-coach": { x: 15.4, y: 64 },
    "operations-coach": { x: 25.5, y: 64 },
    "technology-coach": { x: 35.8, y: 64 },
    "marketing-coach": { x: 15, y: 77.5 },
    "finance-coach": { x: 25.5, y: 77.5 },
    "hr-coach": { x: 35.8, y: 77.5 },
    "session-agent": { x: 67.3, y: 63.8 },
    "accountability-agent": { x: 77.5, y: 63.8 },
    "scorecard-agent": { x: 87.8, y: 63.8 },
    "pulse-agent": { x: 66.5, y: 77.8 },
    "renewal-agent": { x: 76.9, y: 77.8 },
    "community-agent": { x: 87.6, y: 77.8 },
    "client-manager": { x: 50, y: 50, manager: true },
  },
  internal: {
    "billing-and-collections": { x: 13.9, y: 21.5 },
    "bookkeeping-agent": { x: 24.4, y: 21 },
    "finance-reporter": { x: 34.5, y: 21 },
    "contracts-and-records": { x: 75.6, y: 22.5 },
    "internal-ops-manager": { x: 50, y: 47, manager: true },
    "team-and-tools-ops": { x: 14.4, y: 68 },
    "knowledge-base-agent": { x: 28.9, y: 68 },
    "portal-chief": { x: 13.9, y: 83 },
    "event-ops-agent": { x: 28.9, y: 83 },
    "task-orchestrator": { x: 64.8, y: 72 },
    "deliverables-tracker": { x: 75.4, y: 72 },
    "scheduler-agent": { x: 87.3, y: 72 },
  },
};

// ---- courier routes ----
// Little agents walk these waypoint chains carrying an item, house to house,
// following the bridges. from/to are agent ids; `to` pulses on delivery.
export const ROUTES = {
  leads: [
    { from: "prospect-miner", to: "qualifier", item: "📇", label: "New lead → Qualifier",
      pts: [[17.8,27.5],[30,36],[38.5,42],[49.5,52],[60.5,62],[71.5,67.5]] },
    { from: "social-listener", to: "outbound-sequencer", item: "✉️", label: "Warm signal → Outreach",
      pts: [[28.3,27],[38.5,42],[49.5,52],[62,42.5],[64.8,24.5]] },
    { from: "qualifier", to: "booking-agent", item: "📅", label: "Qualified lead → Booking",
      pts: [[71.5,67.5],[76,67.5],[80.4,67.5]] },
    { from: "call-intelligence", to: "program-builder", item: "📄", label: "Call notes → Program Builder",
      pts: [[57.2,81],[65.8,81],[74.3,81]] },
    { from: "program-builder", to: "lead-manager", item: "📜", label: "Proposal → Lead Manager",
      pts: [[74.3,81],[67,72],[60.5,62],[49.5,50]] },
    { from: "newsletter-agent", to: "campaign-agent", item: "📰", label: "Draft issue → Campaign",
      pts: [[16.2,65],[20,72],[26.2,78.7]] },
    { from: "lead-manager", to: "stage-nurture", item: "🤝", label: "Signed client → handoff east",
      pts: [[49.5,50],[60.5,62],[74,72],[82.8,81],[92,72]] },
  ],
  clients: [
    { from: "lead-manager", to: "client-manager", item: "🤝", label: "New client arriving from Leads World",
      pts: [[2,56.5],[6.5,54.5],[17,51.8],[30,50.5],[43.5,52],[50,50]] },
    { from: "client-manager", to: "onboarding-agent", item: "📦", label: "Kickoff packet → Onboarding",
      pts: [[50,50],[43.5,39.5],[34,30],[24,25],[17.8,21.5]] },
    { from: "intake-agent", to: "systems-setup-agent", item: "🗂️", label: "Cost codes → Systems Setup",
      pts: [[28.8,21],[34,21],[39.7,21]] },
    { from: "assessment-and-goals", to: "roadmap-and-priorities", item: "📊", label: "Analysis → Roadmap",
      pts: [[70.9,21.5],[77,21.5],[82.8,21.5]] },
    { from: "roadmap-and-priorities", to: "session-agent", item: "🗺️", label: "90-day roadmap → Sessions",
      pts: [[82.8,21.5],[70,30],[60,39.5],[52,52],[60,60.5],[67.3,63.8]] },
    { from: "scorecard-agent", to: "renewal-agent", item: "📈", label: "Health score → Renewal",
      pts: [[87.8,63.8],[82,70],[76.9,77.8]] },
  ],
  internal: [
    { from: "bookkeeping-agent", to: "finance-reporter", item: "🧾", label: "Reconciled books → Finance",
      pts: [[24.4,21],[29.5,21],[34.5,21]] },
    { from: "contracts-and-records", to: "internal-ops-manager", item: "📜", label: "Signed contract → Ops Manager",
      pts: [[75.6,22.5],[65,32],[56,42],[50,47]] },
    { from: "internal-ops-manager", to: "billing-and-collections", item: "💰", label: "New engagement → Billing",
      pts: [[50,47],[42,36],[30,27],[13.9,21.5]] },
    { from: "task-orchestrator", to: "deliverables-tracker", item: "✅", label: "Dispatched task → Tracker",
      pts: [[64.8,72],[70,72],[75.4,72]] },
    { from: "knowledge-base-agent", to: "internal-ops-manager", item: "📚", label: "Indexed knowledge → Ops Manager",
      pts: [[28.9,68],[38,58],[45,52],[50,47]] },
  ],
};

// ---- drawn bridge overlays ----
// The clients art ships with a dangling bridge at the far-left edge (the
// lead-to-client transition). This walkway connects it to the center island,
// which already bridges to Onboarding. viewBox is 0 0 1448 1086.
export const BRIDGES = {
  clients: {
    d: "M55,600 C250,556 470,542 632,566",
    ropeTop: "M55,588 C250,544 470,530 632,554",
    ropeBottom: "M55,612 C250,568 470,554 632,578",
    sign: { text: "⬅ From Leads World", left: 3, top: 48.5 },
  },
};
